import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetClose } from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CalendarDays, MapPin, Tag, Clock, ExternalLink, Award, Flag, Users, X, Mail, Link as LinkIcon, Share2, Copy, Check } from 'lucide-react';
import { Event } from '@/lib/types';
import { getEventDateRangeString } from '@/lib/eventHelpers';
import EventFeedbackForm from './EventFeedbackForm';
import { loadProfessions } from '@/services/professionService';
import { Profession } from '@/lib/professionTypes';
import { eventTypeLabels } from '@/lib/eventLabels';
import StatusBadge from './admin/StatusBadge';
import { EventStatus } from '@/services/eventService';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useToast } from '@/hooks/use-toast';
import { useEventTracking } from '@/hooks/useEventTracking';

interface EventDetailsDrawerProps {
  event: Event | null;
  open: boolean;
  onClose: () => void;
}

const EventDetailsDrawer = ({ event, open, onClose }: EventDetailsDrawerProps) => {
  const [isFeedbackFormOpen, setIsFeedbackFormOpen] = useState(false);
  const [professions, setProfessions] = useState<Profession[]>([]);
  const [loading, setLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const { toast } = useToast();
  const { trackEventView, trackEventClick } = useEventTracking();

  useEffect(() => {
    const fetchProfessions = async () => {
      if (open && event?.professions && event.professions.length > 0) {
        setLoading(true);
        try {
          const allProfessions = await loadProfessions();
          setProfessions(allProfessions);
        } catch (error) {
          console.error('Hiba a szakmák betöltésekor:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchProfessions();
  }, [open, event]);

  useEffect(() => {
    if (open) {
      setIsCopied(false);
      // Track event view when drawer opens
      if (event?.id) {
        console.log('EventDetailsDrawer opened, tracking view for event:', event.id);
        trackEventView(event.id);
      }
    }
  }, [open, event?.id, trackEventView]);

  if (!event) return null;

  const dateRange = getEventDateRangeString(event.dateStart, event.dateEnd);

  const getProfessionName = (id: string): string => {
    const profession = professions.find(p => p.id === id);
    return profession ? profession.name : id;
  };

  const isEmail = event.registrationUrl &&
    /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(event.registrationUrl);
  const isInternalLink = event.registrationUrl &&
    !isEmail && event.registrationUrl.startsWith('/');
  const isExternalLink = event.registrationUrl &&
    !isEmail && !isInternalLink;

  const getEventUrl = () => {
    const eventIdentifier = event.slug || event.code;
    return `${window.location.origin}/calendar/event/${eventIdentifier}`;
  };

  const copyEventLink = () => {
    const url = getEventUrl();
    navigator.clipboard.writeText(url)
      .then(() => {
        setIsCopied(true);
        toast({
          title: "Link másolva!",
          description: "Az esemény linkje a vágólapra került.",
          variant: "default"
        });
        setTimeout(() => setIsCopied(false), 3000);
      })
      .catch((err) => {
        console.error('Hiba a link másolásakor:', err);
        toast({
          title: "Hiba történt",
          description: "A link másolása sikertelen.",
          variant: "destructive"
        });
      });
  };

  const renderRegistrationInfo = () => {
    if (!event.registrationUrl) return null;

    if (isEmail) {
      return (
        <Button className="flex-1 flex items-center gap-2 bg-medihub-primary text-white hover:bg-medihub-primary/90 shadow-md"
          onClick={() => {
            navigator.clipboard.writeText(event.registrationUrl || '');
          }}
        >
          <Mail className="h-4 w-4" />
          <span>E-mail másolása</span>
        </Button>
      );
    } else if (isInternalLink) {
      return (
        <Button className="flex-1 bg-medihub-accent hover:bg-medihub-accent/90 text-white shadow-md">
          <Link 
            to={event.registrationUrl} 
            className="w-full h-full flex items-center justify-center gap-2"
            onClick={() => {
              const eventId = event.id || event.code || event['@id'];
              if (eventId) {
                trackEventClick(String(eventId), 'registration_click');
              }
            }}
          >
            <LinkIcon className="h-4 w-4" />
            Regisztráció
          </Link>
        </Button>
      );
    } else {
      return (
        <Button className="flex-1 bg-medihub-accent hover:bg-medihub-accent/90 text-white shadow-md">
          <a
            href={event.registrationUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="w-full h-full flex items-center justify-center gap-2"
            onClick={() => {
              const eventId = event.id || event.code || event['@id'];
              if (eventId) {
                trackEventClick(String(eventId), 'registration_click');
              }
            }}
          >
            <ExternalLink className="h-4 w-4" />
            Regisztráció
          </a>
        </Button>
      );
    }
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg md:max-w-xl overflow-hidden flex flex-col border-l border-medihub-primary/10">
        <SheetClose className="absolute right-4 top-4 rounded-full w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 transition-colors z-10">
          <X className="h-5 w-5" />
          <span className="sr-only">Bezárás</span>
        </SheetClose>

        <SheetHeader className="mb-4 pt-2 pb-4 border-b ">
          <div className="flex justify-between items-start">
            <SheetTitle className="text-xl font-bold text-gray-900 pr-6">{event.name}</SheetTitle>
            {event.status && (
              <StatusBadge status={event.status as EventStatus || EventStatus.DRAFT} />
            )}
          </div>

          <div className="flex flex-wrap gap-2 mt-3">
            {event.type && (
              <Badge variant="outline" className="px-3 py-1.5 text-sm font-medium bg-gray-100 text-gray-800 rounded-full">
                {eventTypeLabels[event.type as keyof typeof eventTypeLabels] || event.type}
              </Badge>
            )}

            {(event.creditPoints !== undefined && event.creditPoints > 0) && (
              <Badge className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 flex items-center gap-1.5 rounded-full">
                <Award className="h-3.5 w-3.5" />
                <span>{event.creditPoints} kreditpont</span>
              </Badge>
            )}

            {(!event.creditPoints || event.creditPoints === 0) && event.postAccredited && (
              <Badge className="px-3 py-1.5 text-sm font-medium bg-blue-100 text-blue-700 flex items-center gap-1.5 rounded-full">
                <Award className="h-3.5 w-3.5" />
                <span>Utóakkreditált</span>
              </Badge>
            )}
          </div>
        </SheetHeader>

        <ScrollArea className="flex-1 overflow-y-auto pr-4">
          <div className="space-y-6">
            <div className="flex items-start gap-3">
              <CalendarDays className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Dátum és időpont</h3>
                <p className="text-gray-600">{dateRange}</p>
              </div>
            </div>

            {event.venue && <div className="flex items-start gap-3">
              <MapPin className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-medium text-gray-900">Helyszín</h3>
                <p className="text-gray-600">
                  {event.venue.online ? 'Online esemény' : `${event.venue.city}, ${event.venue.name}`}
                </p>
                {!event.venue.online && <p className="text-gray-500">{event.venue.address}</p>}
              </div>
            </div>}

            {event.maxAttendees && (
              <div className="flex items-start gap-3">
                <Users className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Tervezett létszám</h3>
                  <p className="text-gray-600">
                    {event.maxAttendees} fő
                    {event.currentParticipants ? ` (eddig ${event.currentParticipants} résztvevő)` : ''}
                  </p>
                </div>
              </div>
            )}

            {event.professions && event.professions.length > 0 && (
              <div className="flex items-start gap-3">
                <Tag className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Szakmacsoportok</h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {loading ? (
                      <div className="text-sm text-gray-500">Szakmák betöltése...</div>
                    ) : (
                      event.professions.map((professionId) => (
                        <Badge key={professionId} variant="outline" className="bg-gray-50 rounded-full">
                          {getProfessionName(professionId)}
                        </Badge>
                      ))
                    )}
                  </div>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <Share2 className="h-5 w-5 text-medihub-accent flex-shrink-0 mt-0.5" />
              <div className="w-full">
                <h3 className="font-medium text-gray-900">Megosztás</h3>
                <div className="flex items-center mt-2 w-full">
                  <div className="bg-gray-100 rounded-l-md py-2 px-3 flex-grow text-sm text-gray-600 truncate">
                    {getEventUrl()}
                  </div>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={copyEventLink}
                          className="rounded-l-none h-9"
                          variant="default"
                        >
                          {isCopied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>{isCopied ? 'Másolva!' : 'Link másolása'}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </div>
            </div>

            {event.registrationUrl && (
              <div className="flex items-start gap-3">
                {isEmail ? (
                  <Mail className="h-5 w-5 text-medihub-primary flex-shrink-0 mt-0.5" />
                ) : (
                  <LinkIcon className="h-5 w-5 text-medihub-primary flex-shrink-0 mt-0.5" />
                )}
                <div>
                  <h3 className="font-medium text-gray-900">
                    {isEmail ? 'Regisztrációs e-mail' : 'Regisztrációs információ'}
                  </h3>
                  <a className="text-gray-600 break-all underline"
                    href={event.registrationUrl}
                    target='_blank'
                    rel="noopener noreferrer"
                  >
                    {event.registrationUrl}
                  </a>
                </div>
              </div>
            )}

            <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Leírás</h3>
              <p className="text-gray-600 whitespace-pre-line">{event.description}</p>
            </div>

            {event.organizer && <div className="border-t pt-4">
              <h3 className="font-medium text-gray-900 mb-2">Szervező</h3>
              <p className="text-gray-600">{event.organizer.name}</p>
              {event.organizer.contactEmail && (
                <p className="text-gray-500 text-sm">Email: {event.organizer.contactEmail}</p>
              )}
              {event.organizer.contactPhone && (
                <p className="text-gray-500 text-sm">Telefon: {event.organizer.contactPhone}</p>
              )}
              {event.organizer.website && (
                <a
                  href={event.organizer.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-medihub-primary text-sm flex items-center mt-1 hover:underline"
                >
                  <ExternalLink className="h-3.5 w-3.5 mr-1" />
                  Szervező weboldala
                </a>
              )}
            </div>}

            {event.tags && event.tags.length > 0 && (
              <div className="flex items-start gap-3">
                <Tag className="h-5 w-5 text-medihub-primary flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-medium text-gray-900">Címkék</h3>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {event.tags.map((tag) => (
                      <Badge key={tag} variant="outline" className="bg-gray-50">
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {event.agenda && event.agenda.length > 0 && (
              <div className="border-t pt-4">
                <h3 className="font-medium text-gray-900 mb-2">Program</h3>
                <div className="space-y-3">
                  {event.agenda.map((item, index) => (
                    <div key={index} className="border-l-2 border-medihub-primary pl-3 py-1">
                      <div className="flex items-center mb-1">
                        <Clock className="h-4 w-4 text-medihub-primary mr-1" />
                        <span className="text-sm font-medium text-gray-600">
                          {item.startTime} - {item.endTime}
                        </span>
                      </div>
                      <h4 className="font-medium text-gray-800">{item.title}</h4>
                      {item.description && (
                        <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        <div className="border-t pt-4 mt-4 flex flex-col sm:flex-row gap-3 sticky bottom-0 bg-white">
          {event.registrationUrl && renderRegistrationInfo()}

          <Button
            variant="outline"
            className="flex-1 flex items-center gap-1 border-orange-200 text-orange-600 hover:bg-orange-50"
            onClick={() => setIsFeedbackFormOpen(true)}
          >
            <Flag className="h-4 w-4 text-orange-500" />
            Probléma jelentése
          </Button>
        </div>
      </SheetContent>

      {event && (
        <EventFeedbackForm
          event={event}
          isOpen={isFeedbackFormOpen}
          onClose={() => setIsFeedbackFormOpen(false)}
        />
      )}
    </Sheet>
  );
};

export default EventDetailsDrawer;
